import alert_mixin from '@/utils/mixins/alert_mixin';
import axios from 'axios';
import {
  CREATE_FORM_URL,
  CHANGE_FORM_STATUS_URL,
  GET_FORMS_URL,
  get_request_config,
} from '@/utils/system_constants/constant.js';
import {
  extract_error_message,
  extract_items,
  parse_response_data,
} from '@/utils/api_response_helpers';

const FORM_TYPE_GUIDANCE = new Map([
  ['Entity Form', 'Best for capturing entity-specific details such as profile data, registration information, and structured records.'],
  ['Profile Form', 'Use this when you need a user or entity profile with general background information and reusable details.'],
  ['Consent Form', 'Choose this for approvals, permissions, declarations, or any workflow that needs explicit user consent.'],
  ['Questionnaire', 'Ideal for surveys, assessments, or guided question flows where the user answers a defined set of prompts.'],
  ['Onboarding Checklist', 'Use this to track step-by-step onboarding tasks, approvals, and completion progress.'],
  ['Compliance Form', 'Best for compliance evidence, control checks, attestations, and audit-friendly submissions.'],
  ['Asset Form', 'Use this when the form is meant to register, review, or manage assets and their related details.'],
]);
const FORM_STATUS_ICONS = new Map([
  ['active', 'bi bi-check2-circle'],
  ['inactive', 'bi bi-file-earmark-text'],
  ['draft', 'bi bi-pencil-square'],
]);
const FORM_STATUS_BADGES = new Map([
  ['active', 'text-success bg-success-subtle'],
  ['inactive', 'text-secondary bg-secondary-subtle'],
  ['draft', 'text-warning bg-warning-subtle'],
]);


export default {
  name: "CreateForm",
  mixins: [alert_mixin],
  data: () => ({
    active_forms: [],
    active_forms_loading: false,
    active_tab: 'forms',
    form_name: "",
    form_description: "",
    form_type_id: "",
    form_is_active: false,
    create_form_running: false,
    modal_instance: null,
    modal_error_message: "",
  }),
  computed: {

    all_forms() {
      return this.active_forms.filter((form) => form.is_active);
    },
    archived_history_forms() {
      return this.active_forms.filter((form) => !form.is_active);
    },
    form_types() {
      return this.$store.getters.formTypes;
    },
    form_types_loading() {
      return this.$store.getters.formMetadataLoading;
    },
    selected_form_type_help() {
      const selected_type = this.form_types.find((type) => String(type.id) === String(this.form_type_id));
      const form_type_name = String(selected_type?.form_type || '').trim();

      if (!form_type_name) {
        return "Choose the form type that best matches the process you want to capture.";
      }

      return FORM_TYPE_GUIDANCE.get(form_type_name)
        || `Use ${form_type_name} for workflows that fit its purpose and required business process.`;
    },
  },
  created() {
    this.load_initial_data();
  },
  methods: {
    normalize_form_name(value) {
      return String(value ?? '')
        .trim()
        .replace(/\s+/g, ' ');
    },

    map_form_to_card(form) {
      const raw_status = String(form?.status ?? '').trim();
      const is_active = form?.is_active != null
        ? Boolean(form.is_active)
        : !['inactive', 'in-active', 'archived'].includes(raw_status.toLowerCase().replace(/[\s_]+/g, '-'));
      const status = is_active ? 'Active' : 'Archived';
      const normalized_status = is_active ? 'active' : 'inactive';
      const form_type = form?.form_type?.form_type || form?.form_type?.name || 'Form';
      const title = form?.form_name || 'Untitled Form';
      const description = form?.form_description || 'No description provided.';
      const last_edited = form?.date_created || 'Unknown date';

      const result = {
        id: form?.id ?? `${title}-${last_edited}`,
        title,
        description,
        last_edited,
        status: status || 'Unknown',
        status_normalized: normalized_status,
        is_active,
        is_status_updating: Boolean(form?.is_status_updating),
        icon: FORM_STATUS_ICONS.get(normalized_status) || 'bi bi-file-earmark-text',
        icon_wrapper_class: '',
        status_badge_class: FORM_STATUS_BADGES.get(normalized_status) || 'text-primary bg-primary-subtle',
        form_type_label: form_type,
      };

      return result;
    },

    set_active_tab(tab) {
      this.active_tab = tab;
    },

    open_create_form_modal() {
      this.modal_error_message = "";
      this.form_name = "";
      this.form_description = "";
      this.form_type_id = String(this.form_types?.[0]?.id ?? '');
      this.form_is_active = false;
    },

    async load_initial_data() {
      try {
        await Promise.all([
          this.$store.dispatch("ensureFormMetadataLoaded"),
          this.load_forms(),
        ]);
      } catch (error) {
        console.warn("Failed to load form data", error);
      }
    },

    async load_forms() {
      this.active_forms_loading = true;

      try {
        const response = await axios.get(GET_FORMS_URL, get_request_config());
        const forms = extract_items(response);
        this.active_forms = Array.isArray(forms)
          ? forms.map((form) => this.map_form_to_card(form))
          : [];
      } catch (error) {
        this.active_forms = [];
        this.show_error(extract_error_message(error, 'Failed to load forms.'));
      } finally {
        this.active_forms_loading = false;
      }
    },

    async create_form() {
      if (this.create_form_running) {
        return;
      }

      this.modal_error_message = "";
      const normalized_form_name = this.normalize_form_name(this.form_name);

      if (!normalized_form_name) {
        this.modal_error_message = "Please enter a form name.";
        return;
      }

      if (!this.form_type_id) {
        this.modal_error_message = "Please select a form type.";
        return;
      }

      this.create_form_running = true;
      const loader = await this.show_loader("Creating form...");

      try {
        const payload = {
          form_name: normalized_form_name,
          form_description: this.form_description.trim(),
          form_type_id: Number(this.form_type_id),
          is_active: Boolean(this.form_is_active),
        };

        const response = await axios.post(CREATE_FORM_URL, payload, get_request_config());
        const parsed_response = parse_response_data(response);

        if (parsed_response?.status === "success") {
          this.show_success(parsed_response.message || "Form created successfully.");
          this.close_modal("create_form");
          this.form_name = "";
          this.form_description = "";
          this.form_type_id = String(this.form_types?.[0]?.id ?? '');
          this.active_tab = payload.is_active ? 'forms' : 'archive';
          this.form_is_active = false;
          await this.load_forms();
          return;
        }

        if (String(parsed_response?.message || '').toLowerCase().includes('already exists')) {
          await this.load_forms();
        }

        this.show_error(parsed_response?.message || "Failed to create form.");
      } catch (error) {
        const message = extract_error_message(error, 'Failed to create form.');

        if (String(message).toLowerCase().includes('already exists')) {
          await this.load_forms();
        }

        this.show_error(message);
      } finally {
        await this.close_loader(loader);
        this.create_form_running = false;
      }
    },

    replace_form_card(form) {
      const mapped_form = this.map_form_to_card(form);
      const existing_index = this.active_forms.findIndex((item) => String(item.id) === String(mapped_form.id));

      if (existing_index === -1) {
        this.active_forms = [mapped_form, ...this.active_forms];
        return;
      }

      this.active_forms.splice(existing_index, 1, mapped_form);
    },

    set_form_status_updating(form_id, is_status_updating) {
      const form = this.active_forms.find((item) => String(item.id) === String(form_id));
      if (form) {
        form.is_status_updating = is_status_updating;
      }
    },

    async toggle_form_active(item, event) {
      const next_is_active = Boolean(event?.target?.checked);

      if (!next_is_active) {
        this.show_confirm(
          'Archive Form?',
          'This form will no longer be available for new dynamic form submissions. Existing submissions will not be affected.',
          async () => {
            await this.update_form_status(item, false);
          },
          () => {
            if (event?.target) {
              event.target.checked = true;
            }
          }
        );
        return;
      }

      await this.update_form_status(item, true);
    },

    async update_form_status(item, is_active, success_message = null) {
      const form_id = Number(item?.id);
      if (!this.can_update_form_status(item, form_id)) {
        return;
      }

      this.set_form_status_updating(form_id, true);

      try {
        const parsed_response = await this.submit_form_status_update(form_id, is_active);
        const response_form = parsed_response?.data && Object.keys(parsed_response.data).length
          ? parsed_response.data
          : { ...item, is_active };
        this.replace_form_card(response_form);
        this.active_tab = is_active ? 'forms' : 'archive';
        this.show_success(this.form_status_success_message(is_active, success_message));
      } catch (error) {
        this.show_error(extract_error_message(error, 'Failed to update form status.'));
      } finally {
        this.set_form_status_updating(form_id, false);
      }
    },

    can_update_form_status(item, form_id) {
      return Number.isFinite(form_id) && form_id > 0 && !item?.is_status_updating;
    },

    async submit_form_status_update(form_id, is_active) {
      const response = await axios.post(
        CHANGE_FORM_STATUS_URL,
        {
          form_id: String(form_id),
          status_value: is_active ? 'activate' : 'deactivate',
        },
        get_request_config()
      );
      const parsed_response = parse_response_data(response);

      if (parsed_response?.status && parsed_response.status !== 'success') {
        throw new Error(parsed_response?.message || 'Failed to update form status.');
      }

      return parsed_response;
    },

    form_status_success_message(is_active, success_message) {
      return success_message || (is_active ? 'Form activated successfully.' : 'Form archived successfully.');
    },

    async restore_form(item) {
      await this.update_form_status(item, true, 'Form restored successfully.');
    },
  },

};
